import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, CalendarIcon, PhoneIcon, ZapIcon, HeartPulseIcon, ClipboardCheckIcon } from 'lucide-react';
export function HeroSection() {
  const benefits = [{
    icon: ZapIcon,
    text: 'Pain & inflammation support'
  }, {
    icon: HeartPulseIcon,
    text: 'Gentle, non-invasive sessions'
  }, {
    icon: ClipboardCheckIcon,
    text: 'Personalized plan after evaluation'
  }];
  return <section className="relative overflow-hidden bg-brand-navy py-8 md:py-10 lg:py-12" data-id="element-153">
      {/* Background Gradients & Glows */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-charcoal to-brand-navy z-0" data-id="element-154" />
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-brand-teal/15 rounded-full blur-[120px] z-0 pointer-events-none" data-id="element-155" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-brand-teal/5 rounded-full blur-[100px] z-0 pointer-events-none" data-id="element-156" />

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-157">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center" data-id="element-158">
          {/* Left Side: Copy & Value Prop */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-7 text-left" data-id="element-159">
            {/* High-contrast badge */}
            <div className="inline-flex items-center gap-2 py-1.5 px-3.5 rounded-full bg-brand-teal text-white text-[11px] md:text-xs font-black tracking-widest mb-4 uppercase shadow-md shadow-brand-teal/30" data-id="element-160">
              <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-161" />
              New Patient Intro Offer
            </div>

            <p className="text-xs md:text-sm font-semibold text-teal-200 mb-3 tracking-wide" data-id="element-162">
              Bloomfield Hills • Aspen Pinnacle Laser Therapy • Over 300 5-Star
              Reviews
            </p>

            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.25rem] font-black text-white mb-3 leading-[1.05] tracking-tight" data-id="element-163">
              Laser Therapy for{' '}
              <span className="text-brand-teal" data-id="element-164">
                Pain Relief & Faster Recovery
              </span>
            </h1>

            <p className="text-base md:text-lg text-teal-50 mb-4 leading-snug font-medium max-w-2xl" data-id="element-165">
              Take the next step toward relief with a simple consultation at
              Paragon Chiropractic in Bloomfield Hills.
            </p>

            <p className="text-sm md:text-base text-teal-100/80 mb-5 leading-relaxed max-w-2xl" data-id="element-166">
              Advanced Aspen laser therapy may help support pain relief, calm
              inflammation, improve movement, and help your body recover —
              without surgery or downtime for most patients.
            </p>

            {/* Compact benefit chips - horizontal row */}
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-167">
              {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2 + idx * 0.08
            }} className="flex items-center gap-2 bg-white/8 backdrop-blur-sm border border-white/15 rounded-full px-3.5 py-1.5" data-id="element-168">
                  <benefit.icon className="w-4 h-4 text-brand-teal flex-shrink-0" data-id="element-169" />
                  <span className="text-xs md:text-sm font-semibold text-white whitespace-nowrap" data-id="element-170">
                    {benefit.text}
                  </span>
                </motion.div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-3" data-id="element-171">
              <a href="#consultation-form" className="w-full sm:w-auto px-6 py-3 bg-brand-teal hover:bg-brand-teal-dark text-white rounded-lg font-bold text-sm md:text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-brand-teal/30" data-id="element-172">
                <CalendarIcon className="w-4 h-4" data-id="element-173" />
                Request My Laser Consultation
              </a>
              <a href="tel:+12485902195" className="w-full sm:w-auto px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/25 rounded-lg font-bold text-sm md:text-base transition-all duration-300 flex items-center justify-center gap-2 backdrop-blur-sm" data-id="element-174">
                <PhoneIcon className="w-4 h-4" data-id="element-175" />
                Call (248) 590-2195
              </a>
            </div>

            <p className="text-xs md:text-sm text-teal-100/70 italic" data-id="element-176">
              No pressure. No guesswork. Just a clear next step.
            </p>
          </motion.div>

          {/* Right Side: Compact Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="lg:col-span-5 w-full max-w-md mx-auto lg:ml-auto" id="consultation-form" data-id="element-177">
            <div className="bg-white rounded-xl p-5 md:p-6 shadow-2xl relative overflow-hidden border border-white/20" data-id="element-178">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-brand-teal" data-id="element-179"></div>

              <div className="inline-block py-0.5 px-2.5 rounded-full bg-brand-teal/10 text-brand-teal-dark text-[10px] font-black tracking-widest uppercase mb-2 mt-1" data-id="element-180">
                Free Consultation
              </div>
              <h2 className="text-lg md:text-xl font-black text-brand-navy mb-1 leading-tight" data-id="element-181">
                Request Your Laser Therapy Consultation
              </h2>
              <p className="text-gray-600 text-xs mb-4 leading-snug" data-id="element-182">
                Ask about the new patient intro offer and find out if laser
                therapy may be a fit.
              </p>

              <form className="space-y-3" onSubmit={e => e.preventDefault()} data-id="element-183">
                <div data-id="element-184">
                  <input type="text" className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all" placeholder="Full Name" data-id="element-185" />
                </div>

                <div className="grid grid-cols-2 gap-2.5" data-id="element-186">
                  <input type="tel" className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all" placeholder="Phone" data-id="element-187" />
                  <input type="email" className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all" placeholder="Email" data-id="element-188" />
                </div>

                <div className="grid grid-cols-2 gap-2.5" data-id="element-189">
                  <select className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all appearance-none" data-id="element-190">
                    <option value="" data-id="element-191">Best time...</option>
                    <option value="morning" data-id="element-192">Morning</option>
                    <option value="afternoon" data-id="element-193">Afternoon</option>
                    <option value="evening" data-id="element-194">Evening</option>
                  </select>
                  <select className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all appearance-none" data-id="element-195">
                    <option value="" data-id="element-196">Area of interest...</option>
                    <option value="back-neck" data-id="element-197">Back / Neck</option>
                    <option value="shoulder-hip-knee" data-id="element-198">
                      Shoulder / Hip / Knee
                    </option>
                    <option value="foot-heel" data-id="element-199">Foot / Heel</option>
                    <option value="sports-injury" data-id="element-200">
                      Sports / Overuse Injury
                    </option>
                    <option value="arthritis" data-id="element-201">
                      Arthritis / Joint Stiffness
                    </option>
                    <option value="general" data-id="element-202">General Question</option>
                  </select>
                </div>

                <textarea rows={2} className="w-full bg-brand-gray-light border border-gray-200 rounded-md px-3 py-2 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all resize-none" placeholder="Briefly describe what you'd like help with... (optional)" data-id="element-203"></textarea>

                <button className="w-full px-6 py-3 bg-brand-teal hover:bg-brand-teal-dark text-white rounded-lg font-black text-sm md:text-base transition-all duration-300 shadow-md shadow-brand-teal/30 flex items-center justify-center gap-2" data-id="element-204">
                  <CalendarIcon className="w-4 h-4" data-id="element-205" />
                  Request My Consultation
                </button>

                <p className="text-[10px] text-gray-500 text-center leading-snug" data-id="element-206">
                  Please do not submit Protected Health Information (PHI)
                  through this form.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}